/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Recipe library. Search is hybrid (keyword + semantic) where the server
 * supports it, and degrades to keyword silently when pgvector isn't present —
 * the badge says which happened rather than pretending.
 *
 * ── THE HEADER USED TO HAVE FIVE BUTTONS ────────────────────────────────────
 * Write one · Scan a page · Import a URL · Select to share · Generate. Four
 * ways to create the same object, offered simultaneously with no ranking, plus
 * a bulk-share mode among them. Now: one primary (`Add recipe`, opening
 * AddRecipeSheet) and an overflow menu. No creation route was removed — see
 * the note in AddRecipeSheet.tsx, which is emphatic about this.
 *
 * ── AND THE CARD ACTIONS WERE INVISIBLE ON TOUCH ────────────────────────────
 * Favourite and archive lived behind `opacity-0 group-hover:opacity-100`. A
 * phone has no hover, so on the device most likely to be in someone's hand in a
 * kitchen those two controls did not exist. They now use `.reveal`, which is
 * visible by default and only hides itself where a fine pointer can bring it
 * back.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { RecipesApi } from "../../api/endpoints.ts";
import { AllergenBadge, Empty, ErrorBox, Loading, PageHeader } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { ActionMenu } from "../../components/ActionMenu.tsx";
import { useToast } from "../../components/Toast.tsx";
import { ShareRecipesModal } from "./ShareRecipesModal.tsx";
import { RecipeImage } from "./RecipeImage.tsx";
import { AddRecipeSheet } from "./AddRecipeSheet.tsx";
import type { RecipeSummary } from "../../api/types.ts";

/** Matches MAX_RECIPES_PER_SHARE in server/sharing.ts. */
const MAX_SHARE = 50;

const SORTS = [
  { key: "recent", label: "Recent" },
  { key: "leastRecentlyCooked", label: "Not made lately" },
  { key: "mostCooked", label: "Made most" },
  { key: "quickest", label: "Quickest" },
  { key: "title", label: "A–Z" },
] as const;

export function RecipesView() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<typeof SORTS[number]["key"]>("recent");
  const [semantic, setSemantic] = useState(true);
  const qc = useQueryClient();
  const toast = useToast();

  // `?add=1` opens the sheet on arrival, so NextCard and GettingStarted can
  // deliver the user into the task rather than merely onto the screen.
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (params.get("add") === "1") {
      setAdding(true);
      params.delete("add");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // ── selection mode ────────────────────────────────────────────────────────
  // Off by default. The cards are links first: making every card permanently
  // checkbox-fronted would tax the common case (open a recipe) to serve the
  // rare one (send a few to a friend).
  const [selecting, setSelecting] = useState(false);
  // The chosen recipes themselves, not just their ids. Searching mid-selection
  // replaces `results` entirely; keying the selection to the visible list would
  // quietly drop everything the current results don't contain — you'd pick
  // three, search for a fourth, and send one.
  const [selected, setSelected] = useState<Map<string, { id: string; title: string }>>(new Map());
  const [sharing, setSharing] = useState(false);

  const toggle = (r: RecipeSummary) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(r.id)) next.delete(r.id);
    else if (next.size >= MAX_SHARE) {
      toast(`You can share at most ${MAX_SHARE} recipes at once.`, "danger");
      return prev;
    } else next.set(r.id, { id: r.id, title: r.title });
    return next;
  });

  const exitSelecting = () => { setSelecting(false); setSelected(new Map()); };

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["recipes"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const onErr = (e: Error) => toast(e.message, "danger");

  const favourite = useMutation({
    mutationFn: (v: { id: string; next: boolean }) =>
      RecipesApi.patch(v.id, { isFavorite: v.next }),
    onSuccess: invalidate, onError: onErr,
  });
  // Archive, not hard-delete: a recipe on a past meal plan is part of the
  // household's cook history.
  const archive = useMutation({
    mutationFn: (rid: string) => RecipesApi.remove(rid),
    onSuccess: () => { invalidate(); toast("Archived", "ok"); }, onError: onErr,
  });

  const browse = useQuery({
    queryKey: ["recipes", "list", sort],
    queryFn: () => RecipesApi.list({ sort, limit: 100 }),
    enabled: q.trim().length === 0,
  });

  const search = useMutation({
    mutationFn: (query: string) =>
      RecipesApi.search({ q: query, mode: semantic ? "hybrid" : "keyword", limit: 40 }),
  });

  const results: RecipeSummary[] = q.trim()
    ? (search.data?.results ?? [])
    : (browse.data?.recipes ?? []);

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Recipes"
        subtitle="Everything your household cooks from. Search plainly — it understands descriptions."
        actions={
          <>
            <ActionMenu
              items={[
                {
                  label: selecting ? "Cancel selection" : "Select to share",
                  icon: "users",
                  hint: selecting ? undefined : "Send recipes to another household",
                  onSelect: () => (selecting ? exitSelecting() : setSelecting(true)),
                },
              ]}
            />
            <button className="btn-primary" onClick={() => setAdding(true)}>
              <Icon name="plus" className="h-4 w-4" />
              Add recipe
            </button>
          </>
        }
      />

      {adding && <AddRecipeSheet onClose={() => setAdding(false)} />}

      {selecting && (
        // A sticky action bar rather than a modal: you scroll while choosing,
        // and the count has to stay visible while you do.
        <div className="sticky top-0 z-10 mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/30 bg-accent-soft px-3 py-2 text-sm">
          <span className="text-ink-2">
            {selected.size === 0
              ? "Pick the recipes to send"
              : `${selected.size} selected`}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="btn-ghost"
              disabled={results.length === 0}
              onClick={() => setSelected((prev) => {
                // Adds to the selection rather than replacing it, so "search,
                // select all, search again, select all" accumulates the way
                // it looks like it should.
                const next = new Map(prev);
                for (const r of results) {
                  if (next.size >= MAX_SHARE) break;
                  next.set(r.id, { id: r.id, title: r.title });
                }
                return next;
              })}
            >Select all shown</button>
            <button className="btn-ghost" disabled={!selected.size}
                    onClick={() => setSelected(new Map())}>Clear</button>
            <button className="btn-primary" disabled={!selected.size}
                    onClick={() => setSharing(true)}>
              Share {selected.size || ""}
            </button>
          </div>
        </div>
      )}

      {sharing && (
        <ShareRecipesModal
          recipes={[...selected.values()].sort((a, b) => a.title.localeCompare(b.title))}
          onClose={() => setSharing(false)}
          onShared={exitSelecting}
        />
      )}

      <form
        className="mb-3 flex gap-2"
        onSubmit={(e) => { e.preventDefault(); if (q.trim()) search.mutate(q.trim()); }}
      >
        <div className="relative flex-1">
          <Icon name="search"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
          <input
            className="input pl-9"
            placeholder="Try 'that thing with lemon and orzo'"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search recipes"
          />
        </div>
        <button className="btn-ghost" disabled={!q.trim() || search.isPending}>Search</button>
      </form>

      {/* Sort as chips rather than a <select>. One tap instead of two, the
          current choice is readable without opening anything, and it stops
          being a 44px-wide target on a phone. */}
      {!q.trim() && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {SORTS.map((s) => (
            <button key={s.key} type="button" className="chip-toggle"
                    aria-pressed={sort === s.key}
                    onClick={() => setSort(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {q.trim() && search.data && (
        <div className="mb-4 flex items-center gap-2 text-xs text-ink-3">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={semantic}
              onChange={(e) => { setSemantic(e.target.checked); if (q.trim()) search.mutate(q.trim()); }} />
            Semantic search
          </label>
          {!search.data.semanticAvailable && (
            <span className="chip border-warn/40 text-warn">
              keyword only — vector search unavailable
            </span>
          )}
        </div>
      )}

      {(browse.isLoading || search.isPending) && <Loading />}
      {browse.error && <ErrorBox message={(browse.error as Error).message} />}
      {search.error && <ErrorBox message={(search.error as Error).message} />}

      {!browse.isLoading && !search.isPending && results.length === 0 && (
        q.trim() ? (
          // The one legitimate actionless empty state: the way out is the
          // search box already on screen and already focused-adjacent.
          <Empty title="Nothing matched" hint="Try fewer words." action={null} />
        ) : (
          <Empty
            title="No recipes yet"
            hint="Paste a link to any recipe on the web and it'll be read for you — most sites
                  publish structured data we can import exactly."
            action={
              <button className="btn-primary" onClick={() => setAdding(true)}>
                <Icon name="plus" className="h-4 w-4" />
                Add your first recipe
              </button>
            }
          />
        )
      )}

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {results.map((r) => {
          const isSelected = selected.has(r.id);

          const body = (
            <>
              {/* Actions sit on the card itself so favouriting or archiving does
                  not require opening the recipe first. `.reveal` keeps them
                  visible on touch and on keyboard focus, and hides them only
                  where a mouse can bring them back. preventDefault stops the
                  surrounding Link from navigating. Hidden while selecting:
                  archiving a recipe you're in the middle of choosing is not a
                  thing anyone means to do. */}
              {!selecting && (
                <div className="reveal absolute right-1 top-1 flex">
                  <button
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-3 transition hover:bg-paper-3 hover:text-accent"
                    aria-label={r.is_favorite ? `Remove ${r.title} from favourites` : `Add ${r.title} to favourites`}
                    title={r.is_favorite ? "Remove from favourites" : "Add to favourites"}
                    onClick={(e) => {
                      e.preventDefault();
                      favourite.mutate({ id: r.id, next: !r.is_favorite });
                    }}
                  >
                    <Icon name={r.is_favorite ? "starFilled" : "star"}
                          className={`h-4 w-4 ${r.is_favorite ? "text-accent" : ""}`} />
                  </button>
                  <button
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-ink-3 transition hover:bg-danger/10 hover:text-danger"
                    aria-label={`Archive ${r.title}`}
                    title="Archive"
                    onClick={(e) => {
                      e.preventDefault();
                      if (confirm(`Archive "${r.title}"?`)) archive.mutate(r.id);
                    }}
                  >
                    <Icon name="archive" className="h-4 w-4" />
                  </button>
                </div>
              )}
              {/* ── THE LAYOUT SCALES TO THE CONTENT ──────────────────────
                  A recipe with a photo leads with it; one without is a text
                  card, not a text card with a grey rectangle where a picture
                  should be. Most libraries are mostly text — typed-in recipes,
                  sites that publish no image — and a grid of "no image"
                  placeholders reads as broken rather than as plain.

                  `has_image` comes from the listing, so the card knows which
                  layout to use before any bytes are requested. */}
              {r.has_image && (
                <div className="-mx-3 -mt-3 mb-3">
                  <RecipeImage recipeId={r.id} aspect="hero" rounded={false} />
                </div>
              )}
              <div className="flex items-start justify-between gap-2 pr-16">
                <span className="flex items-start gap-2 font-serif text-lg leading-snug text-ink">
                  {selecting && (
                    <input
                      type="checkbox"
                      className="mt-1.5 shrink-0"
                      checked={isSelected}
                      readOnly
                      tabIndex={-1}
                      aria-hidden
                    />
                  )}
                  {r.title}
                </span>
              </div>
              {r.description && (
                <p className="mt-1 line-clamp-2 text-sm text-ink-3">{r.description}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1 text-xs text-ink-3">
                {/* A persistent favourite marker, separate from the toggle
                    above. The toggle lives in `.reveal` and is therefore
                    hidden until hover on a desktop — without this, whether a
                    recipe is a favourite would only be visible while pointing
                    at it, which is exactly backwards. */}
                {r.is_favorite && (
                  <Icon name="starFilled" className="h-3.5 w-3.5 text-accent" label="Favourite" />
                )}
                {r.total_minutes ? <span>{r.total_minutes} min</span> : null}
                <span>· {r.servings_base} servings</span>
                {/* Computed server-side in the household timezone. */}
                <span>· {r.last_cooked_label}</span>
              </div>
              <div className="mt-2">
                <AllergenBadge tags={r.allergen_tags} partial={r.allergens_partial} />
              </div>
            </>
          );

          // While selecting, the card is a toggle, not a link — a <button>
          // rather than a Link with preventDefault, so keyboard and screen
          // reader users get told what it actually does.
          return selecting ? (
            <button
              key={r.id}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              onClick={() => toggle(r)}
              className={`card group relative overflow-hidden p-3 text-left transition ${
                isSelected ? "border-accent ring-1 ring-accent" : "hover:border-accent"
              }`}
            >{body}</button>
          ) : (
            <Link
              key={r.id}
              to={`/recipes/${r.id}`}
              className="card group relative overflow-hidden p-3 hover:border-accent"
            >{body}</Link>
          );
        })}
      </div>
    </div>
  );
}
